import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Download, FileText, DollarSign, Building, ShoppingCart, Eye } from 'lucide-react';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';
import {
  fetchProfitReport,
  fetchProfitReportByBranch,
  fetchProfitStatementReport,
  fetchPayablesReport,
  fetchSalesByBranchesReport,
  fetchDailySalesReport,
  fetchSuppliers,
  fetchBranches,
  fetchItems,
  fetchSettings,
  getApiBaseUrl,
} from '@/api';
import { MonthYearPicker } from '@/components/ui/month-year-picker';
import { StatementDetailDialog } from '@/components/reports/StatementDetailDialog';
import { formatInTimeZone } from 'date-fns-tz';

interface ProfitReportItem {
  item_id: string;
  name?: string | null;
  quantity: number;
  display_quantity?: number;
  buying_price: number;
  selling_price: number;
  profit: number;
}

interface ProfitReportSaleDetail {
  type?: 'sale';
  sale_id: string;
  invoice_number?: string | number | null;
  created_at: string;
  subtotal?: number;
  discount_type?: 'percentage' | 'fixed' | null;
  discount_value?: number;
  discount_amount?: number;
  total_amount: number;
  cost: number;
  profit: number;
  items: ProfitReportItem[];
}

interface ProfitReportLayawayDetail {
  type: 'layaway_payment';
  payment_id: string;
  layaway_id: string;
  layaway_number?: string | number | null;
  payment_ratio?: number;
  created_at: string;
  total_amount: number;
  cost: number;
  profit: number;
  items: ProfitReportItem[];
}

type ProfitReportDetail = ProfitReportSaleDetail | ProfitReportLayawayDetail;

interface ProfitReport {
  summary: {
    total_revenue: number;
    total_cost: number;
    total_profit: number;
  };
  details?: ProfitReportDetail[];
}

interface ProfitStatementPeriod {
  period_id: string;
  label: string;
  start_date: string;
  end_date: string;
  revenue: {
    sales: number;
    layaway: number;
    total: number;
  };
  cost_of_sales: {
    sales: number;
    layaway: number;
    total: number;
  };
  gross_profit: number;
  expenses: {
    category_id: string;
    category_name: string;
    total: number;
  }[];
  total_expenses: number;
  net_profit: number;
  supplier_payments: {
    category_name: string;
    total: number;
  };
}

interface ProfitStatementReport {
  period_type: 'monthly' | 'quarterly' | 'half-yearly' | 'yearly';
  periods: ProfitStatementPeriod[];
}

interface ProfitByBranchReport {
  summary: {
    total_revenue: number;
    total_cost: number;
    total_profit: number;
  };
  branches: {
    branch_id: string;
    branch_name: string;
    total_revenue: number;
    total_cost: number;
    total_profit: number;
    sales_count: number;
    layaway_payments_count: number;
  }[];
}

interface PayablesReport {
  summary?: {
    total_payables: number;
    total_paid: number;
    total_due: number;
  };
  details?: {
    invoice_id: string;
    supplier_id: string;
    supplier_name: string;
    invoice_number: string;
    date: string;
    due_date: string;
    amount: number;
    paid_amount: number;
    remaining_amount: number;
    status: string;
    attachment_path?: string;
  }[];
}

type BranchSaleDetail =
  | {
      type: 'sale';
      sale_id: string;
      invoice_number?: string | number | null;
      created_at: string;
      total_amount: number;
      discount_type?: 'percentage' | 'fixed' | null;
      discount_value?: number;
      discount_amount?: number;
    }
  | {
      type: 'layaway_payment';
      payment_id: string;
      layaway_number?: string | number | null;
      created_at: string;
      total_amount: number;
    };

interface SalesByBranchesReport {
  summary?: {
    total_sales_amount: number;
    total_transactions: number;
    branches: number;
  };
  details?: {
    branch_id: string;
    branch_name: string;
    total_amount: number;
    transactions: number;
    sales: BranchSaleDetail[];
  }[];
}

interface SalesByItemReport {
  summary?: {
    total_profit: number;
    total_items: number;
  };
  details?: {
    item_id: string;
    name: string;
    total_quantity_sold: number;
    average_buying_price: number;
    average_selling_price: number;
    total_profit: number;
  }[];
}

interface DailySalesReport {
  report_date: string;
  timezone: string;
  day_name: string;
  branch_id?: string | null;
  branch_name?: string | null;
  sales: Array<{
    sale_id: string;
    invoice_number?: string | number | null;
    branch_id?: string | null;
    branch_name?: string | null;
    created_at: string;
    description: string;
    cash_amount: number;
    bank_amount: number;
  }>;
  expenses: Array<{
    expense_id: string;
    description: string;
    branch_id?: string | null;
    branch_name?: string | null;
    expense_date: string;
    cash_amount: number;
    bank_amount: number;
  }>;
  summary: {
    cash_sales_total: number;
    bank_sales_total: number;
    total_sales: number;
    cash_expenses_total: number;
    bank_expenses_total: number;
    cash_in_hand_remaining: number;
    bank_remaining: number;
  };
}

interface Supplier {
  id: string;
  name: string;
}

interface Branch {
  id: string;
  name: string;
}

interface CompanyReportSettings {
  companyName: string;
  phone: string;
  email: string;
  address: string;
}

export default function ReportsPage() {
  const [searchParams] = useSearchParams();
  const currencySymbol = useCurrencySymbol();
  useEffect(() => {
    document.title = 'Reports - Vixalor';
  }, []);
  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: 12 }, (_, index) => currentYear - 5 + index);
  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];
  const [profitReport, setProfitReport] = useState<ProfitReport | null>(null);
  const [profitByBranchReport, setProfitByBranchReport] = useState<ProfitByBranchReport | null>(null);
  const [profitStatementReport, setProfitStatementReport] = useState<ProfitStatementReport | null>(null);
  const [payablesReport, setPayablesReport] = useState<PayablesReport | null>(null);
  const [salesByBranchesReport, setSalesByBranchesReport] = useState<SalesByBranchesReport | null>(null);
  const [salesByItemReport, setSalesByItemReport] = useState<SalesByItemReport | null>(null);
  const [dailySalesReport, setDailySalesReport] = useState<DailySalesReport | null>(null);

  const [profitStartDate, setProfitStartDate] = useState<Date | undefined>(undefined);
  const [profitEndDate, setProfitEndDate] = useState<Date | undefined>(undefined);
  const [profitBranchStartDate, setProfitBranchStartDate] = useState<Date | undefined>(undefined);
  const [profitBranchEndDate, setProfitBranchEndDate] = useState<Date | undefined>(undefined);
  const [salesStartDate, setSalesStartDate] = useState<Date | undefined>(undefined);
  const [salesEndDate, setSalesEndDate] = useState<Date | undefined>(undefined);
  const [dailyReportDate, setDailyReportDate] = useState<Date | undefined>(new Date());
  const [itemPeriodType, setItemPeriodType] = useState<'daily' | 'monthly' | 'custom'>('daily');
  const [itemDailyDate, setItemDailyDate] = useState<Date | undefined>(new Date());
  const [itemMonthDate, setItemMonthDate] = useState<Date | undefined>(new Date());
  const [itemStartDate, setItemStartDate] = useState<Date | undefined>(undefined);
  const [itemEndDate, setItemEndDate] = useState<Date | undefined>(undefined);
  const [selectedSupplier, setSelectedSupplier] = useState<string>('all');
  const [selectedBranch, setSelectedBranch] = useState<string>('all');
  const [statementPeriodType, setStatementPeriodType] = useState<'monthly' | 'quarterly' | 'half-yearly' | 'yearly'>('monthly');
  const [statementPeriods, setStatementPeriods] = useState<Array<{ year: number; month?: number; quarter?: number; half?: number }>>([
    { year: new Date().getFullYear(), month: new Date().getMonth() + 1 },
  ]);

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [items, setItems] = useState<{ id: string; name: string }[]>([]);
  const [systemTimezone, setSystemTimezone] = useState<string>('Africa/Windhoek');
  const [companySettings, setCompanySettings] = useState<CompanyReportSettings>({
    companyName: 'Company',
    phone: '',
    email: '',
    address: '',
  });
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'profit');

  // Statement detail dialog state
  const [detailDialog, setDetailDialog] = useState<{
    open: boolean;
    type: 'sales' | 'layaway' | 'expense';
    startDate: string;
    endDate: string;
    label: string;
    categoryId?: string;
    categoryName?: string;
  }>({
    open: false,
    type: 'sales',
    startDate: '',
    endDate: '',
    label: '',
  });
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch suppliers, branches, and items on mount
  useEffect(() => {
    async function loadData() {
      try {
        const [suppliersRes, branchesRes, itemsRes, settingsRes] = await Promise.all([
          fetchSuppliers(),
          fetchBranches(),
          fetchItems(),
          fetchSettings(),
        ]);
        setSuppliers(suppliersRes.data);
        setBranches(branchesRes.data);
        setItems(itemsRes.data.map(item => ({ id: item.id, name: item.name })));
        setSystemTimezone(settingsRes.data?.timezone || 'Africa/Windhoek');
        setCompanySettings({
          companyName:
            settingsRes.data?.company_name ||
            settingsRes.data?.business_name ||
            settingsRes.data?.store_name ||
            'Company',
          phone:
            settingsRes.data?.company_phone ||
            settingsRes.data?.phone ||
            settingsRes.data?.business_phone ||
            '',
          email: settingsRes.data?.email || '',
          address: settingsRes.data?.address || '',
        });
      } catch (err: any) {
        toast.error('Failed to load suppliers, branches, or items');
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    const date = searchParams.get('date');
    if (tab) {
      setActiveTab(tab);
    }
    if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-').map(Number);
      setDailyReportDate(new Date(year, month - 1, day));
    }
  }, [searchParams]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    const date = searchParams.get('date');

    if (tab !== 'daily-sales' || !date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return;
    }

    const loadLinkedDailyReport = async () => {
      try {
        setLoading(true);
        const branch = selectedBranch === 'all' ? '' : selectedBranch;
        const response = await fetchDailySalesReport(date, branch);
        setDailySalesReport(response.data);
      } catch (err: any) {
        toast.error('Failed to load daily sales report');
      } finally {
        setLoading(false);
      }
    };

    loadLinkedDailyReport();
  }, [searchParams, selectedBranch]);

  const buildStatementPeriodId = (period: { year: number; month?: number; quarter?: number; half?: number }) => {
    if (statementPeriodType === 'monthly') {
      const month = String(period.month ?? 1).padStart(2, '0');
      return `${period.year}-${month}`;
    }
    if (statementPeriodType === 'quarterly') {
      return `${period.year}-Q${period.quarter ?? 1}`;
    }
    if (statementPeriodType === 'half-yearly') {
      return `${period.year}-H${period.half ?? 1}`;
    }
    return `${period.year}`;
  };

  const createDefaultStatementPeriod = (type: typeof statementPeriodType) => {
    const monthIndex = new Date().getMonth() + 1;
    if (type === 'monthly') {
      return { year: currentYear, month: monthIndex };
    }
    if (type === 'quarterly') {
      return { year: currentYear, quarter: Math.ceil(monthIndex / 3) };
    }
    if (type === 'half-yearly') {
      return { year: currentYear, half: monthIndex <= 6 ? 1 : 2 };
    }
    return { year: currentYear };
  };

  const updateStatementPeriodType = (value: 'monthly' | 'quarterly' | 'half-yearly' | 'yearly') => {
    setStatementPeriodType(value);
    setStatementPeriods([createDefaultStatementPeriod(value)]);
    setProfitStatementReport(null);
  };

  const updateStatementPeriod = (index: number, updates: Partial<{ year: number; month: number; quarter: number; half: number }>) => {
    setStatementPeriods(prev => prev.map((period, idx) => (idx === index ? { ...period, ...updates } : period)));
  };

  const addStatementPeriod = () => {
    setStatementPeriods(prev => {
      if (prev.length >= 4) {
        toast.error('You can compare up to 4 periods.');
        return prev;
      }
      return [...prev, createDefaultStatementPeriod(statementPeriodType)];
    });
  };

  const removeStatementPeriod = (index: number) => {
    setStatementPeriods(prev => prev.filter((_, idx) => idx !== index));
  };

  const getSalesByItemDateRange = () => {
    if (itemPeriodType === 'daily') {
      const selected = itemDailyDate ?? new Date();
      const value = format(selected, 'yyyy-MM-dd');
      return { start: value, end: value };
    }

    if (itemPeriodType === 'monthly') {
      const selected = itemMonthDate ?? new Date();
      const year = selected.getFullYear();
      const month = selected.getMonth();
      const start = format(new Date(year, month, 1), 'yyyy-MM-dd');
      const end = format(new Date(year, month + 1, 0), 'yyyy-MM-dd');
      return { start, end };
    }

    if (!itemStartDate || !itemEndDate) {
      toast.error('Please select custom start and end dates.');
      return null;
    }

    return {
      start: format(itemStartDate, 'yyyy-MM-dd'),
      end: format(itemEndDate, 'yyyy-MM-dd'),
    };
  };

  const formatReportTimestamp = (value: string) => {
    try {
      const parsed = new Date(
        !value.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(value)
          ? `${value}Z`
          : value
      );
      if (Number.isNaN(parsed.getTime())) return value;
      return formatInTimeZone(parsed, systemTimezone, 'M/d/yyyy, h:mm:ss a');
    } catch {
      return value;
    }
  };

  // Fetch Profit Report
  const loadProfitReport = async () => {
    try {
      setLoading(true);
      const start = profitStartDate ? format(profitStartDate, 'yyyy-MM-dd') : '';
      const end = profitEndDate ? format(profitEndDate, 'yyyy-MM-dd') : '';
      const response = await fetchProfitReport(start, end);
      setProfitReport(response.data);
    } catch (err: any) {
      toast.error('Failed to load profit report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Profit by Branch Report
  const loadProfitByBranchReport = async () => {
    try {
      setLoading(true);
      const start = profitBranchStartDate ? format(profitBranchStartDate, 'yyyy-MM-dd') : '';
      const end = profitBranchEndDate ? format(profitBranchEndDate, 'yyyy-MM-dd') : '';
      const response = await fetchProfitReportByBranch(start, end);
      setProfitByBranchReport(response.data);
    } catch (err: any) {
      toast.error('Failed to load profit by branch report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Payables Report
  const loadPayablesReport = async () => {
    try {
      setLoading(true);
      const supplierId = selectedSupplier === 'all' ? '' : selectedSupplier;
      const response = await fetchPayablesReport(supplierId);
      setPayablesReport(response.data);
    } catch (err: any) {
      toast.error('Failed to load payables report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Sales by Branches Report
  const loadSalesByBranchesReport = async () => {
    try {
      setLoading(true);
      const start = salesStartDate ? format(salesStartDate, 'yyyy-MM-dd') : '';
      const end = salesEndDate ? format(salesEndDate, 'yyyy-MM-dd') : '';
      const branch = selectedBranch === 'all' ? '' : selectedBranch;
      const response = await fetchSalesByBranchesReport(start, end, branch);
      setSalesByBranchesReport(response.data);
    } catch (err: any) {
      toast.error('Failed to load sales by branches report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Sales by Item Report
  const loadSalesByItemReport = async () => {
    try {
      setLoading(true);
      const range = getSalesByItemDateRange();
      if (!range) {
        setLoading(false);
        return;
      }

      const response = await fetchProfitReport(range.start, range.end);
      const details = response.data?.details ?? [];

      const itemAggregates = new Map<
        string,
        {
          item_id: string;
          name: string;
          quantity: number;
          weighted_buying_total: number;
          weighted_selling_total: number;
          total_profit: number;
        }
      >();

      details.forEach(detail => {
        detail.items.forEach(item => {
          const quantity = Number(item.display_quantity ?? item.quantity ?? 0);
          const key = item.item_id;
          const existing = itemAggregates.get(key) ?? {
            item_id: key,
            name: item.name ?? '—',
            quantity: 0,
            weighted_buying_total: 0,
            weighted_selling_total: 0,
            total_profit: 0,
          };

          existing.quantity += quantity;
          existing.weighted_buying_total += Number(item.buying_price ?? 0) * quantity;
          existing.weighted_selling_total += Number(item.selling_price ?? 0) * quantity;
          existing.total_profit += Number(item.profit ?? 0);
          if (!existing.name || existing.name === '—') {
            existing.name = item.name ?? existing.name;
          }

          itemAggregates.set(key, existing);
        });
      });

      const aggregatedDetails = Array.from(itemAggregates.values())
        .map(item => ({
          item_id: item.item_id,
          name: item.name,
          total_quantity_sold: item.quantity,
          average_buying_price: item.quantity > 0 ? item.weighted_buying_total / item.quantity : 0,
          average_selling_price: item.quantity > 0 ? item.weighted_selling_total / item.quantity : 0,
          total_profit: item.total_profit,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setSalesByItemReport({
        summary: {
          total_profit: aggregatedDetails.reduce((sum, item) => sum + item.total_profit, 0),
          total_items: aggregatedDetails.length,
        },
        details: aggregatedDetails,
      });
    } catch (err: any) {
      toast.error('Failed to load sales by item report');
    } finally {
      setLoading(false);
    }
  };

  const loadDailySalesReport = async () => {
    try {
      setLoading(true);
      const date = format(dailyReportDate ?? new Date(), 'yyyy-MM-dd');
      const branch = selectedBranch === 'all' ? '' : selectedBranch;
      const response = await fetchDailySalesReport(date, branch);
      setDailySalesReport(response.data);
    } catch (err: any) {
      toast.error('Failed to load daily sales report');
    } finally {
      setLoading(false);
    }
  };

  const downloadDailySalesPdf = () => {
    if (!dailySalesReport) {
      return;
    }

    const averageSaleValue = dailySalesReport.summary.total_sales / Math.max(dailySalesReport.sales.length, 1);
    const money = (value: number) => `${currencySymbol}${value.toFixed(2)}`;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 12;
    let cursorY = 16;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42);
    doc.text(companySettings.companyName || 'Company', pageWidth / 2, cursorY, { align: 'center' });
    doc.setFontSize(12);
    doc.text(`END OF DAY REPORT (${dailySalesReport.branch_name || 'ALL BRANCHES'})`, pageWidth / 2, cursorY + 7, { align: 'center' });
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`${dailySalesReport.report_date} (${dailySalesReport.day_name})`, pageWidth / 2, cursorY + 13, { align: 'center' });
    doc.setDrawColor(203, 213, 225);
    doc.line(margin, cursorY + 16, pageWidth - margin, cursorY + 16);
    cursorY += 20;

    autoTable(doc, {
      startY: cursorY,
      margin: { left: margin, right: margin },
      head: [['Cash Sales', 'Bank / SP Sales', 'Total Sales', 'Cash Remaining']],
      body: [[
        money(dailySalesReport.summary.cash_sales_total),
        money(dailySalesReport.summary.bank_sales_total),
        money(dailySalesReport.summary.total_sales),
        money(dailySalesReport.summary.cash_in_hand_remaining),
      ]],
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3, lineColor: [203, 213, 225], lineWidth: 0.1 },
      headStyles: { fillColor: [248, 250, 252], textColor: [15, 23, 42], fontStyle: 'bold', halign: 'center' },
      bodyStyles: { textColor: [15, 23, 42], halign: 'center', fontStyle: 'bold' },
    });

    cursorY = (doc as any).lastAutoTable.finalY + 4;

    cursorY = (doc as any).lastAutoTable.finalY + 4;

    autoTable(doc, {
      startY: cursorY,
      margin: { left: margin, right: margin },
      head: [['Sales Breakdown', 'Cash', 'SP / Bank']],
      body: dailySalesReport.sales.length
        ? [
            ...dailySalesReport.sales.map(row => [
              row.description,
              row.cash_amount > 0 ? money(row.cash_amount) : '-',
              row.bank_amount > 0 ? money(row.bank_amount) : '-',
            ]),
            ['Sales Total', money(dailySalesReport.summary.cash_sales_total), money(dailySalesReport.summary.bank_sales_total)],
          ]
        : [['No sales found for the selected date.', '', '']],
      theme: 'grid',
      headStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42], fontStyle: 'bold' },
      styles: { fontSize: 8.5, cellPadding: 2.5, lineColor: [203, 213, 225], lineWidth: 0.1 },
      columnStyles: {
        0: { cellWidth: 134 },
        1: { cellWidth: 26, halign: 'right' },
        2: { cellWidth: 26, halign: 'right' },
      },
      didParseCell: (data) => {
        if (dailySalesReport.sales.length && data.section === 'body' && data.row.index === dailySalesReport.sales.length) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [241, 245, 249];
        }
      },
    });

    cursorY = (doc as any).lastAutoTable.finalY + 4;

    autoTable(doc, {
      startY: cursorY,
      margin: { left: margin, right: margin },
      head: [['Expenses', 'Cash', 'Bank']],
      body: dailySalesReport.expenses.length
        ? [
            ...dailySalesReport.expenses.map(row => [
              row.description,
              row.cash_amount > 0 ? money(row.cash_amount) : '-',
              row.bank_amount > 0 ? money(row.bank_amount) : '-',
            ]),
            ['Expense Total', money(dailySalesReport.summary.cash_expenses_total), money(dailySalesReport.summary.bank_expenses_total)],
          ]
        : [['No expenses found for the selected date.', '', '']],
      theme: 'grid',
      headStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42], fontStyle: 'bold' },
      styles: { fontSize: 8.5, cellPadding: 2.5, lineColor: [203, 213, 225], lineWidth: 0.1 },
      columnStyles: {
        0: { cellWidth: 134 },
        1: { cellWidth: 26, halign: 'right' },
        2: { cellWidth: 26, halign: 'right' },
      },
      didParseCell: (data) => {
        if (dailySalesReport.expenses.length && data.section === 'body' && data.row.index === dailySalesReport.expenses.length) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [241, 245, 249];
        }
      },
    });

    cursorY = (doc as any).lastAutoTable.finalY + 4;
    if (cursorY > pageHeight - 40) {
      doc.addPage();
      cursorY = 16;
    }

    autoTable(doc, {
      startY: cursorY,
      margin: { left: margin, right: margin },
      head: [['Closing Summary', 'Amount']],
      body: [
        ['Total Sales Collected', money(dailySalesReport.summary.total_sales)],
        ['Cash In Hand Before Expenses', money(dailySalesReport.summary.cash_sales_total)],
        ['Cash Used for Expenses', money(dailySalesReport.summary.cash_expenses_total)],
        ['Average Sale Value', money(averageSaleValue)],
        ['Bank-side Expenses', money(dailySalesReport.summary.bank_expenses_total)],
        ['Bank Net Position', money(dailySalesReport.summary.bank_remaining)],
        ['Cash Remaining', money(dailySalesReport.summary.cash_in_hand_remaining)],
      ],
      theme: 'grid',
      headStyles: {
        fillColor: [241, 245, 249],
        textColor: [15, 23, 42],
        fontStyle: 'bold',
        fontSize: 10,
      },
      bodyStyles: {
        textColor: [15, 23, 42],
        fontSize: 8.5,
      },
      styles: { cellPadding: 2.5, lineColor: [203, 213, 225], lineWidth: 0.1 },
      columnStyles: {
        0: { cellWidth: 130, fontStyle: 'bold' },
        1: { halign: 'right', cellWidth: 56 },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.row.index === 6) {
          data.cell.styles.fillColor = [241, 245, 249];
          data.cell.styles.textColor = [15, 23, 42];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 9.5;
        }
      },
    });

    const pageCount = doc.getNumberOfPages();
    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
      doc.setPage(pageNumber);
      doc.setDrawColor(226, 232, 240);
      doc.line(margin, pageHeight - 10, pageWidth - margin, pageHeight - 10);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(`Generated from POS EOD Report`, margin, pageHeight - 5);
      doc.text(`Page ${pageNumber} of ${pageCount}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
    }

    doc.save(`eod-report-${dailySalesReport.report_date}.pdf`);
  };

  const loadProfitStatementReport = async () => {
    try {
      setLoading(true);
      const periodIds = statementPeriods.map(buildStatementPeriodId);
      const uniqueIds = new Set(periodIds);
      if (uniqueIds.size !== periodIds.length) {
        toast.error('Please select unique periods for comparison.');
        setLoading(false);
        return;
      }
      const response = await fetchProfitStatementReport(statementPeriodType, periodIds);
      setProfitStatementReport(response.data);
    } catch (err: any) {
      toast.error('Failed to load profit statement');
    } finally {
      setLoading(false);
    }
  };

  // Generate PDF for Profit Report
  const downloadProfitReportPDF = () => {
    if (!profitReport || !profitReport.details) return;

    const doc = new jsPDF();
    const margin = 20;
    let cursorY = 20;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Profit Report', margin, cursorY);
    cursorY += 10;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Revenue: ${currencySymbol}${(profitReport.summary?.total_revenue ?? 0).toFixed(2)}`, margin, cursorY);
    cursorY += 10;
    doc.text(`Total Cost: ${currencySymbol}${(profitReport.summary?.total_cost ?? 0).toFixed(2)}`, margin, cursorY);
    cursorY += 10;
    doc.text(`Total Profit: ${currencySymbol}${(profitReport.summary?.total_profit ?? 0).toFixed(2)}`, margin, cursorY);
    cursorY += 20;

    profitReport.details.forEach((detail) => {
      const isLayaway = detail.type === 'layaway_payment';
      let title: string;

      if (isLayaway) {
        title = `Layaway #${detail.layaway_number ?? detail.layaway_id} Payment`;
      } else {
        const saleDetail = detail as ProfitReportSaleDetail;
        title = saleDetail.invoice_number
          ? `Sale Invoice #${saleDetail.invoice_number}`
          : `Sale #${saleDetail.sale_id}`;
      }

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(title, margin, cursorY);
      cursorY += 10;

      autoTable(doc, {
        startY: cursorY,
        head: [['Item Name', 'Quantity', 'Buying Price', 'Selling Price', 'Profit']],
        body: detail.items.map(item => [
          item.name ?? '—',
          (() => {
            if (isLayaway) {
              const base = item.display_quantity ?? item.quantity;
              const coverage = typeof detail.payment_ratio === 'number'
                ? ` (${(detail.payment_ratio * 100).toFixed(0)}%)`
                : '';
              return `${Number(base).toLocaleString()}${coverage}`;
            }
            return Number(item.quantity).toLocaleString();
          })(),
          `${currencySymbol}${item.buying_price.toFixed(2)}`,
          `${currencySymbol}${item.selling_price.toFixed(2)}`,
          `${currencySymbol}${item.profit.toFixed(2)}`
        ]),
        margin: { left: margin, right: margin },
        styles: { fontSize: 10, cellPadding: 4 },
        headStyles: { fillColor: [33, 150, 243], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
      });

      cursorY = (doc as any).lastAutoTable.finalY + 10;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`${isLayaway ? 'Payment Date' : 'Sale Date'}: ${formatReportTimestamp(detail.created_at)}`, margin, cursorY);
      cursorY += 6;
      doc.text(`${isLayaway ? 'Payment Amount' : 'Total Amount'}: ${currencySymbol}${(detail.total_amount ?? 0).toFixed(2)}`, margin, cursorY);
      cursorY += 6;
      if (!isLayaway) {
        const saleDetail = detail as ProfitReportSaleDetail;
        doc.text(`Discount: ${currencySymbol}${Number(saleDetail.discount_amount ?? 0).toFixed(2)}`, margin, cursorY);
        cursorY += 6;
      }
      doc.text(`Cost: ${currencySymbol}${(detail.cost ?? 0).toFixed(2)}`, margin, cursorY);
      cursorY += 6;
      doc.text(`Profit: ${currencySymbol}${(detail.profit ?? 0).toFixed(2)}`, margin, cursorY);
      cursorY += 15;
    });

    doc.save('profit_report.pdf');
  };

  // Generate PDF for Payables Report
  const downloadPayablesReportPDF = () => {
    if (!payablesReport || !payablesReport.details) return;

    const doc = new jsPDF();
    const margin = 20;
    let cursorY = 20;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Payables to Supplier Report', margin, cursorY);
    cursorY += 10;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Payables: ${currencySymbol}${(payablesReport.summary?.total_payables ?? 0).toFixed(2)}`, margin, cursorY);
    cursorY += 10;
    doc.text(`Total Paid: ${currencySymbol}${(payablesReport.summary?.total_paid ?? 0).toFixed(2)}`, margin, cursorY);
    cursorY += 10;
    doc.text(`Total Due: ${currencySymbol}${(payablesReport.summary?.total_due ?? 0).toFixed(2)}`, margin, cursorY);
    cursorY += 20;

    autoTable(doc, {
      startY: cursorY,
      head: [['Supplier', 'Invoice #', 'Date', 'Due Date', 'Amount', 'Paid', 'Remaining', 'Status']],
      body: payablesReport.details.map(detail => [
        detail.supplier_name,
        detail.invoice_number,
        detail.date,
        detail.due_date,
        `${currencySymbol}${detail.amount.toFixed(2)}`,
        `${currencySymbol}${detail.paid_amount.toFixed(2)}`,
        `${currencySymbol}${detail.remaining_amount.toFixed(2)}`,
        detail.status
      ]),
      margin: { left: margin, right: margin },
      styles: { fontSize: 10, cellPadding: 4 },
      headStyles: { fillColor: [33, 150, 243], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right' }
      }
    });

    doc.save('payables_report.pdf');
  };

  // Generate PDF for Sales by Branches Report
  const downloadSalesByBranchesPDF = () => {
    if (!salesByBranchesReport || !salesByBranchesReport.details) return;

    const doc = new jsPDF();
    const margin = 20;
    let cursorY = 20;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Sales by Branches Report', margin, cursorY);
    cursorY += 10;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Sales Amount: ${currencySymbol}${(salesByBranchesReport.summary?.total_sales_amount ?? 0).toFixed(2)}`, margin, cursorY);
    cursorY += 10;
    doc.text(`Total Transactions: ${salesByBranchesReport.summary?.total_transactions ?? 0}`, margin, cursorY);
    cursorY += 10;
    doc.text(`Branches: ${salesByBranchesReport.summary?.branches ?? 0}`, margin, cursorY);
    cursorY += 20;

    salesByBranchesReport.details.forEach(branch => {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`${branch.branch_name}`, margin, cursorY);
      cursorY += 10;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total Amount: ${currencySymbol}${(branch.total_amount ?? 0).toFixed(2)}`, margin, cursorY);
      cursorY += 6;
      doc.text(`Transactions: ${branch.transactions ?? 0}`, margin, cursorY);
      cursorY += 10;

      autoTable(doc, {
        startY: cursorY,
        head: [['Type', 'Reference', 'Date', 'Discount', 'Amount']],
        body: branch.sales.map(entry => {
          const isLayaway = entry.type === 'layaway_payment';
          const typeLabel = isLayaway ? 'Layaway Payment' : 'Sale';
          const reference = isLayaway
            ? `Layaway #${entry.layaway_number ?? '—'} Payment`
            : entry.invoice_number
              ? `Invoice #${entry.invoice_number}`
              : `Sale #${entry.sale_id}`;
          const discountText = isLayaway
            ? '—'
            : `${currencySymbol}${Number('discount_amount' in entry ? entry.discount_amount ?? 0 : 0).toFixed(2)}`;

          return [
            typeLabel,
            reference,
            formatReportTimestamp(entry.created_at),
            discountText,
            `${currencySymbol}${entry.total_amount.toFixed(2)}`
          ];
        }),
        margin: { left: margin, right: margin },
        styles: { fontSize: 10, cellPadding: 4 },
        headStyles: { fillColor: [33, 150, 243], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: {
          3: { halign: 'right' },
          4: { halign: 'right' }
        }
      });

      cursorY = (doc as any).lastAutoTable.finalY + 15;
    });

    doc.save('sales_by_branches_report.pdf');
  };

  // Generate PDF for Sales by Item Report
  const downloadSalesByItemPDF = () => {
    if (!salesByItemReport || !salesByItemReport.details) return;

    const doc = new jsPDF();
    const margin = 20;
    let cursorY = 20;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Sales by Item Report', margin, cursorY);
    cursorY += 10;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Profit: ${currencySymbol}${(salesByItemReport.summary?.total_profit ?? 0).toFixed(2)}`, margin, cursorY);
    cursorY += 10;
    doc.text(`Total Items: ${salesByItemReport.summary?.total_items ?? 0}`, margin, cursorY);
    cursorY += 12;

    autoTable(doc, {
      startY: cursorY,
      head: [['Item', 'Total Qty Sold', 'Avg Buying Price', 'Avg Selling Price', 'Total Profit']],
      body: salesByItemReport.details.map(item => [
        item.name,
        Number(item.total_quantity_sold).toLocaleString(undefined, { maximumFractionDigits: 2 }),
        `${currencySymbol}${item.average_buying_price.toFixed(2)}`,
        `${currencySymbol}${item.average_selling_price.toFixed(2)}`,
        `${currencySymbol}${item.total_profit.toFixed(2)}`
      ]),
      margin: { left: margin, right: margin },
      styles: { fontSize: 10, cellPadding: 4 },
      headStyles: { fillColor: [33, 150, 243], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' }
      }
    });

    doc.save('sales_by_item_report.pdf');
  };

  const statementPeriodsData = profitStatementReport?.periods ?? [];
  const statementExpenseCategories = Array.from(
    new Map(
      statementPeriodsData
        .flatMap(period => period.expenses)
        .map(expense => [expense.category_id, expense.category_name])
    ).entries()
  ).map(([id, name]) => ({ id, name }));

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold">Reports</h1>
        <p className="text-muted-foreground">Generate and view various reports for your Vixalor system.</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="profit">Profit Report</TabsTrigger>
          <TabsTrigger value="profit-statement">Profit Statement</TabsTrigger>
          <TabsTrigger value="profit-by-branch">Profit by Branch</TabsTrigger>
          <TabsTrigger value="payables">Payables to Supplier</TabsTrigger>
          <TabsTrigger value="sales-branches">Sales by Branches</TabsTrigger>
          <TabsTrigger value="sales-items">Sales by Item</TabsTrigger>
          <TabsTrigger value="daily-sales">Daily / EOD Report</TabsTrigger>
        </TabsList>

        {/* Profit Report Tab */}
        <TabsContent value="profit">
          <Card>
            <CardHeader>
              <CardTitle>Profit Report</CardTitle>
              <CardDescription>View profits from sales over a specified period.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Start Date:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-48 justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {profitStartDate ? format(profitStartDate, 'PPP') : 'Select start date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={profitStartDate}
                        onSelect={setProfitStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">End Date:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-48 justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {profitEndDate ? format(profitEndDate, 'PPP') : 'Select end date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={profitEndDate}
                        onSelect={setProfitEndDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <Button onClick={loadProfitReport} disabled={loading}>
                  {loading ? 'Loading...' : 'Generate Report'}
                </Button>
              </div>

              {profitReport && profitReport.summary && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {currencySymbol}{profitReport.summary.total_revenue.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {currencySymbol}{profitReport.summary.total_cost.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Profit</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {currencySymbol}{profitReport.summary.total_profit.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="mb-4">
                    <Button onClick={downloadProfitReportPDF} disabled={!profitReport.details?.length}>
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF
                    </Button>
                  </div>

                  {profitReport.details?.map(detail => {
                    const isLayaway = detail.type === 'layaway_payment';
                    const cardKey = isLayaway
                      ? `layaway-${detail.payment_id}`
                      : `sale-${(detail as ProfitReportSaleDetail).sale_id}`;

                    const saleDetail = !isLayaway ? (detail as ProfitReportSaleDetail) : null;
                    const headerTitle = isLayaway
                      ? `Layaway #${detail.layaway_number ?? detail.layaway_id} Payment`
                      : saleDetail?.invoice_number
                        ? `Sale Invoice #${saleDetail.invoice_number}`
                        : `Sale #${saleDetail?.sale_id}`;

                    const amountLabel = isLayaway ? 'Payment Amount' : 'Total Amount';
                    const saleDiscountAmount = !isLayaway ? Number(saleDetail?.discount_amount ?? 0) : 0;

                    return (
                      <Card key={cardKey} className="mb-4">
                        <CardHeader>
                          <div className="flex justify-between items-center">
                            <CardTitle>{headerTitle}</CardTitle>
                            <p className="text-sm text-muted-foreground">
                              Date: {formatReportTimestamp(detail.created_at)}
                            </p>
                          </div>
                          <CardDescription>
                            {amountLabel}: {currencySymbol}{detail.total_amount.toFixed(2)}
                            {!isLayaway ? ` | Discount: ${currencySymbol}${saleDiscountAmount.toFixed(2)}` : ''}
                            {' | '}Cost: {currencySymbol}{detail.cost.toFixed(2)} | Profit: {currencySymbol}{detail.profit.toFixed(2)}
                            {isLayaway && typeof detail.payment_ratio === 'number'
                              ? ` | Payment Coverage: ${(detail.payment_ratio * 100).toFixed(0)}%`
                              : ''}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Item Name</TableHead>
                                <TableHead>Quantity</TableHead>
                                <TableHead className="text-right">Buying Price</TableHead>
                                <TableHead className="text-right">Selling Price</TableHead>
                                <TableHead className="text-right">Profit</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {detail.items.map(item => {
                                const baseQuantity = isLayaway
                                  ? item.display_quantity ?? item.quantity
                                  : item.quantity;
                                const coverageText = isLayaway && typeof detail.payment_ratio === 'number'
                                  ? ` (${(detail.payment_ratio * 100).toFixed(0)}%)`
                                  : '';
                                const quantityDisplay = `${Number(baseQuantity).toLocaleString()}${coverageText}`;

                                return (
                                  <TableRow key={item.item_id}>
                                    <TableCell>{item.name ?? '—'}</TableCell>
                                    <TableCell>{quantityDisplay}</TableCell>
                                    <TableCell className="text-right">{currencySymbol}{item.buying_price.toFixed(2)}</TableCell>
                                    <TableCell className="text-right">{currencySymbol}{item.selling_price.toFixed(2)}</TableCell>
                                    <TableCell className="text-right">{currencySymbol}{item.profit.toFixed(2)}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    );
                  })}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profit Statement Tab */}
        <TabsContent value="profit-statement">
          <Card>
            <CardHeader>
              <CardTitle>Profit Statement</CardTitle>
              <CardDescription>Compare revenue, costs, and expenses across periods.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4 mb-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Period Type</label>
                    <Select
                      value={statementPeriodType}
                      onValueChange={value => updateStatementPeriodType(value as 'monthly' | 'quarterly' | 'half-yearly' | 'yearly')}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Select period type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="half-yearly">Half-Yearly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" onClick={addStatementPeriod} disabled={loading}>
                    Add Comparison
                  </Button>
                  <Button onClick={loadProfitStatementReport} disabled={loading}>
                    Generate Statement
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Includes approved company expenses and net sales after returns.
                </p>

                {statementPeriods.map((period, index) => (
                  <div key={`${period.year}-${index}`} className="flex flex-wrap items-end gap-3">
                    {statementPeriodType === 'monthly' && (
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Month & Year</label>
                        <MonthYearPicker
                          year={period.year}
                          month={period.month ?? 1}
                          onYearChange={(year) => updateStatementPeriod(index, { year })}
                          onMonthChange={(month) => updateStatementPeriod(index, { month })}
                          availableYears={availableYears}
                        />
                      </div>
                    )}

                    {statementPeriodType !== 'monthly' && (
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Year</label>
                        <Select
                          value={String(period.year)}
                          onValueChange={value => updateStatementPeriod(index, { year: Number(value) })}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {availableYears.map(year => (
                              <SelectItem key={year} value={String(year)}>
                                {year}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {statementPeriodType === 'quarterly' && (
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Quarter</label>
                        <Select
                          value={String(period.quarter ?? 1)}
                          onValueChange={value => updateStatementPeriod(index, { quarter: Number(value) })}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4].map(quarter => (
                              <SelectItem key={quarter} value={String(quarter)}>
                                Q{quarter}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {statementPeriodType === 'half-yearly' && (
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Half</label>
                        <Select
                          value={String(period.half ?? 1)}
                          onValueChange={value => updateStatementPeriod(index, { half: Number(value) })}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2].map(half => (
                              <SelectItem key={half} value={String(half)}>
                                H{half}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {statementPeriods.length > 1 && (
                      <Button variant="ghost" onClick={() => removeStatementPeriod(index)}>
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {profitStatementReport && statementPeriodsData.length > 0 ? (
                <div className="overflow-x-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-64">Line Item</TableHead>
                        {statementPeriodsData.map(period => (
                          <TableHead key={period.period_id} className="text-right">
                            {period.label}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">Net sales</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <span>{currencySymbol}{period.revenue.sales.toFixed(2)}</span>
                              {period.revenue.sales > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => setDetailDialog({
                                    open: true,
                                    type: 'sales',
                                    startDate: period.start_date,
                                    endDate: period.end_date,
                                    label: `Net Sales - ${period.label}`,
                                  })}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Layaway payments</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <span>{currencySymbol}{period.revenue.layaway.toFixed(2)}</span>
                              {period.revenue.layaway > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => setDetailDialog({
                                    open: true,
                                    type: 'layaway',
                                    startDate: period.start_date,
                                    endDate: period.end_date,
                                    label: `Layaway Payments - ${period.label}`,
                                  })}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow className="bg-muted/40">
                        <TableCell className="font-semibold">Total revenue</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right font-semibold">
                            {currencySymbol}{period.revenue.total.toFixed(2)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Cost of sales (sales)</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right">
                            {currencySymbol}{period.cost_of_sales.sales.toFixed(2)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Cost of sales (layaways)</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right">
                            {currencySymbol}{period.cost_of_sales.layaway.toFixed(2)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow className="bg-muted/40">
                        <TableCell className="font-semibold">Total cost of sales</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right font-semibold">
                            {currencySymbol}{period.cost_of_sales.total.toFixed(2)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow className="bg-muted/50">
                        <TableCell className="font-semibold">Gross profit</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right font-semibold">
                            {currencySymbol}{period.gross_profit.toFixed(2)}
                          </TableCell>
                        ))}
                      </TableRow>

                      <TableRow>
                        <TableCell className="font-semibold">Operating expenses</TableCell>
                        <TableCell colSpan={statementPeriodsData.length} />
                      </TableRow>

                      {statementExpenseCategories.map(category => (
                        <TableRow key={category.id}>
                          <TableCell className="text-muted-foreground">{category.name}</TableCell>
                          {statementPeriodsData.map(period => {
                            const amount = period.expenses.find(expense => expense.category_id === category.id)?.total ?? 0;
                            return (
                              <TableCell key={period.period_id} className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <span>{currencySymbol}{amount.toFixed(2)}</span>
                                  {amount > 0 && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0"
                                      onClick={() => setDetailDialog({
                                        open: true,
                                        type: 'expense',
                                        startDate: period.start_date,
                                        endDate: period.end_date,
                                        label: `${category.name} - ${period.label}`,
                                        categoryId: category.id,
                                        categoryName: category.name,
                                      })}
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}

                      <TableRow className="bg-muted/40">
                        <TableCell className="font-semibold">Total operating expenses</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right font-semibold">
                            {currencySymbol}{period.total_expenses.toFixed(2)}
                          </TableCell>
                        ))}
                      </TableRow>

                      <TableRow className="bg-muted/50">
                        <TableCell className="font-semibold">Net profit</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right font-semibold">
                            {currencySymbol}{period.net_profit.toFixed(2)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">
                          {statementPeriodsData[0]?.supplier_payments?.category_name ?? 'Supplier payments'}
                        </TableCell>
                        {statementPeriodsData.map(period => {
                          const amount = period.supplier_payments?.total ?? 0;
                          return (
                            <TableCell key={period.period_id} className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <span>{currencySymbol}{amount.toFixed(2)}</span>
                                {amount > 0 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => setDetailDialog({
                                      open: true,
                                      type: 'expense',
                                      startDate: period.start_date,
                                      endDate: period.end_date,
                                      label: `Payment to Supplier - ${period.label}`,
                                      categoryId: 'cat_274e0cb02f5bbd4e6085f32e28894fca',
                                      categoryName: 'Payment to Supplier',
                                    })}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Select periods and generate the statement to view results.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profit by Branch Report Tab */}
        <TabsContent value="profit-by-branch">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Profit Report by Branch
              </CardTitle>
              <CardDescription>View profit breakdown across different branches.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Start Date:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-[280px] justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {profitBranchStartDate ? format(profitBranchStartDate, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={profitBranchStartDate}
                        onSelect={setProfitBranchStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">End Date:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-[280px] justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {profitBranchEndDate ? format(profitBranchEndDate, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={profitBranchEndDate}
                        onSelect={setProfitBranchEndDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <Button onClick={loadProfitByBranchReport} disabled={loading}>
                  Generate Report
                </Button>
              </div>

              {profitByBranchReport && (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <DollarSign className="h-4 w-4" />
                          Total Revenue
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="text-2xl font-bold">
                          {currencySymbol}{profitByBranchReport.summary.total_revenue.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <ShoppingCart className="h-4 w-4" />
                          Total Cost
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="text-2xl font-bold text-red-600">
                          {currencySymbol}{profitByBranchReport.summary.total_cost.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Total Profit
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className={`text-2xl font-bold ${profitByBranchReport.summary.total_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {currencySymbol}{profitByBranchReport.summary.total_profit.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Branch Breakdown Table */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Profit by Branch</h3>
                      <Button
                        variant="outline"
                        onClick={() => {
                          const doc = new jsPDF();
                          doc.setFontSize(20);
                          doc.text('Profit Report by Branch', 20, 20);
                          doc.setFontSize(12);
                          doc.text(`Generated: ${format(new Date(), 'PPP')}`, 20, 35);
                          if (profitBranchStartDate) doc.text(`From: ${format(profitBranchStartDate, 'PPP')}`, 20, 45);
                          if (profitBranchEndDate) doc.text(`To: ${format(profitBranchEndDate, 'PPP')}`, 20, 55);
                          
                          const tableData = profitByBranchReport.branches.map(branch => [
                            branch.branch_name,
                            `${currencySymbol}${branch.total_revenue.toFixed(2)}`,
                            `${currencySymbol}${branch.total_cost.toFixed(2)}`,
                            `${currencySymbol}${branch.total_profit.toFixed(2)}`,
                            branch.sales_count.toString(),
                            branch.layaway_payments_count.toString(),
                          ]);

                          autoTable(doc, {
                            head: [['Branch', 'Revenue', 'Cost', 'Profit', 'Sales', 'Layaway Payments']],
                            body: tableData,
                            startY: 65,
                          });

                          doc.save('profit-by-branch-report.pdf');
                          toast.success('Report downloaded successfully');
                        }}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </Button>
                    </div>

                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Branch</TableHead>
                          <TableHead className="text-right">Revenue</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                          <TableHead className="text-right">Profit</TableHead>
                          <TableHead className="text-center">Sales Count</TableHead>
                          <TableHead className="text-center">Layaway Payments</TableHead>
                          <TableHead className="text-right">Profit Margin</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {profitByBranchReport.branches.map((branch, index) => {
                          const profitMargin = branch.total_revenue > 0 
                            ? ((branch.total_profit / branch.total_revenue) * 100).toFixed(1)
                            : '0.0';
                          
                          return (
                            <TableRow key={branch.branch_id}>
                              <TableCell className="font-medium">{branch.branch_name}</TableCell>
                              <TableCell className="text-right">{currencySymbol}{branch.total_revenue.toFixed(2)}</TableCell>
                              <TableCell className="text-right text-red-600">{currencySymbol}{branch.total_cost.toFixed(2)}</TableCell>
                              <TableCell className={`text-right font-semibold ${branch.total_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {currencySymbol}{branch.total_profit.toFixed(2)}
                              </TableCell>
                              <TableCell className="text-center">{branch.sales_count}</TableCell>
                              <TableCell className="text-center">{branch.layaway_payments_count}</TableCell>
                              <TableCell className={`text-right font-medium ${parseFloat(profitMargin) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {profitMargin}%
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payables to Supplier Tab */}
        <TabsContent value="payables">
          <Card>
            <CardHeader>
              <CardTitle>Payables to Supplier Report</CardTitle>
              <CardDescription>View outstanding payments to suppliers.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Supplier:</label>
                  <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="All Suppliers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Suppliers</SelectItem>
                      {suppliers.map(supplier => (
                        <SelectItem key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadPayablesReport} disabled={loading}>
                  {loading ? 'Loading...' : 'Generate Report'}
                </Button>
              </div>

              {payablesReport && payablesReport.summary && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Payables</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {currencySymbol}{payablesReport.summary.total_payables.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Paid</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {currencySymbol}{payablesReport.summary.total_paid.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Due</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {currencySymbol}{payablesReport.summary.total_due.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="mb-4">
                    <Button onClick={downloadPayablesReportPDF} disabled={!payablesReport.details?.length}>
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF
                    </Button>
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Supplier</TableHead>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Paid</TableHead>
                        <TableHead className="text-right">Remaining</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Attachment</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payablesReport.details?.map(detail => (
                        <TableRow key={detail.invoice_id}>
                          <TableCell>{detail.supplier_name}</TableCell>
                          <TableCell>{detail.invoice_number}</TableCell>
                          <TableCell>{detail.date}</TableCell>
                          <TableCell>{detail.due_date}</TableCell>
                          <TableCell className="text-right">{currencySymbol}{detail.amount.toFixed(2)}</TableCell>
                          <TableCell className="text-right">{currencySymbol}{detail.paid_amount.toFixed(2)}</TableCell>
                          <TableCell className="text-right">{currencySymbol}{detail.remaining_amount.toFixed(2)}</TableCell>
                          <TableCell>{detail.status}</TableCell>
                          <TableCell>
                            {detail.attachment_path ? (
                              <a href={`${getApiBaseUrl()}/${detail.attachment_path}`} target="_blank" rel="noopener noreferrer">
                                <FileText className="h-4 w-4" />
                              </a>
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales by Branches Tab */}
        <TabsContent value="sales-branches">
          <Card>
            <CardHeader>
              <CardTitle>Sales by Branches Report</CardTitle>
              <CardDescription>View sales performance across branches.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Start Date:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-48 justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {salesStartDate ? format(salesStartDate, 'PPP') : 'Select start date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={salesStartDate}
                        onSelect={setSalesStartDate}
                        defaultMonth={salesStartDate ?? new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">End Date:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-48 justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {salesEndDate ? format(salesEndDate, 'PPP') : 'Select end date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={salesEndDate}
                        onSelect={setSalesEndDate}
                        defaultMonth={salesEndDate ?? new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Branch:</label>
                  <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="All Branches" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Branches</SelectItem>
                      {branches.map(branch => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadSalesByBranchesReport} disabled={loading}>
                  {loading ? 'Loading...' : 'Generate Report'}
                </Button>
              </div>

              {salesByBranchesReport && salesByBranchesReport.summary && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Sales Amount</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {currencySymbol}{salesByBranchesReport.summary.total_sales_amount.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Transactions</CardTitle>
                        <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {salesByBranchesReport.summary.total_transactions}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Branches</CardTitle>
                        <Building className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {salesByBranchesReport.summary.branches}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="mb-4">
                    <Button onClick={downloadSalesByBranchesPDF} disabled={!salesByBranchesReport.details?.length}>
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF
                    </Button>
                  </div>

                  {salesByBranchesReport.details?.map(branch => (
                    <Card key={branch.branch_id} className="mb-4">
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <CardTitle>{branch.branch_name}</CardTitle>
                          <p className="text-sm text-muted-foreground">
                            Transactions: {branch.transactions}
                          </p>
                        </div>
                        <CardDescription>
                          Total Amount: {currencySymbol}{(branch.total_amount ?? 0).toFixed(2)}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Type</TableHead>
                                <TableHead>Reference</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead className="text-right">Discount</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                              </TableRow>
                            </TableHeader>
                          <TableBody>
                            {branch.sales.map(entry => {
                              const isLayaway = entry.type === 'layaway_payment';
                              const rowKey = isLayaway
                                ? `layaway-${entry.payment_id}`
                                : `sale-${entry.sale_id}`;
                              const typeLabel = isLayaway ? 'Layaway Payment' : 'Sale';
                              const reference = isLayaway
                                ? `Layaway #${entry.layaway_number ?? '—'} Payment`
                                : entry.invoice_number
                                  ? `Invoice #${entry.invoice_number}`
                                  : `Sale #${entry.sale_id}`;
                              const discountText = isLayaway
                                ? '—'
                                : `${currencySymbol}${Number('discount_amount' in entry ? entry.discount_amount ?? 0 : 0).toFixed(2)}`;

                              return (
                                <TableRow key={rowKey}>
                                  <TableCell>{typeLabel}</TableCell>
                                  <TableCell>{reference}</TableCell>
                                  <TableCell>{formatReportTimestamp(entry.created_at)}</TableCell>
                                  <TableCell className="text-right">{discountText}</TableCell>
                                  <TableCell className="text-right">{currencySymbol}{entry.total_amount.toFixed(2)}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales by Item Tab */}
        <TabsContent value="sales-items">
          <Card>
            <CardHeader>
              <CardTitle>Sales by Item Report</CardTitle>
              <CardDescription>View item-level averages and total profit for the selected period.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Period:</label>
                  <Select
                    value={itemPeriodType}
                    onValueChange={value => setItemPeriodType(value as 'daily' | 'monthly' | 'custom')}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {itemPeriodType === 'daily' && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium">Date:</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-48 justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {itemDailyDate ? format(itemDailyDate, 'PPP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={itemDailyDate}
                          onSelect={setItemDailyDate}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

                {itemPeriodType === 'monthly' && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium">Month:</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-48 justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {itemMonthDate ? format(itemMonthDate, 'MMMM yyyy') : 'Select month'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={itemMonthDate}
                          onSelect={setItemMonthDate}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

                {itemPeriodType === 'custom' && (
                  <>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Start Date:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-48 justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {itemStartDate ? format(itemStartDate, 'PPP') : 'Select start date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={itemStartDate}
                        onSelect={setItemStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">End Date:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-48 justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {itemEndDate ? format(itemEndDate, 'PPP') : 'Select end date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={itemEndDate}
                        onSelect={setItemEndDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                  </>
                )}

                <Button onClick={loadSalesByItemReport} disabled={loading}>
                  {loading ? 'Loading...' : 'Generate Report'}
                </Button>
              </div>

              {salesByItemReport && salesByItemReport.summary && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Profit</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {currencySymbol}{salesByItemReport.summary.total_profit.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Items</CardTitle>
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {salesByItemReport.summary.total_items}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="mb-4">
                    <Button onClick={downloadSalesByItemPDF} disabled={!salesByItemReport.details?.length}>
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF
                    </Button>
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item Name</TableHead>
                        <TableHead className="text-right">Total Qty Sold</TableHead>
                        <TableHead className="text-right">Avg Buying Price</TableHead>
                        <TableHead className="text-right">Avg Sell Price</TableHead>
                        <TableHead className="text-right">Total Profit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {salesByItemReport.details?.length ? (
                        salesByItemReport.details.map(item => (
                          <TableRow key={item.item_id}>
                            <TableCell>{item.name}</TableCell>
                            <TableCell className="text-right">
                              {Number(item.total_quantity_sold).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell className="text-right">{currencySymbol}{item.average_buying_price.toFixed(2)}</TableCell>
                            <TableCell className="text-right">{currencySymbol}{item.average_selling_price.toFixed(2)}</TableCell>
                            <TableCell className="text-right">{currencySymbol}{item.total_profit.toFixed(2)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">
                            No items found for selected period.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="daily-sales">
          <Card>
            <CardHeader>
              <CardTitle>Daily / EOD Report</CardTitle>
              <CardDescription>Daily sales with Cash, SP (Bank), and same-day expenses.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Date:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-48 justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dailyReportDate ? format(dailyReportDate, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dailyReportDate}
                        onSelect={setDailyReportDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Branch:</label>
                  <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="All Branches" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Branches</SelectItem>
                      {branches.map(branch => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadDailySalesReport} disabled={loading}>
                  {loading ? 'Loading...' : 'Generate Report'}
                </Button>
                {dailySalesReport && (
                  <Button variant="outline" onClick={downloadDailySalesPdf}>
                    <Download className="mr-2 h-4 w-4" />
                    Download PDF
                  </Button>
                )}
              </div>

              {dailySalesReport && (
                <div className="space-y-6">
                  <div className="overflow-hidden rounded-2xl border bg-gradient-to-r from-slate-950 via-slate-900 to-slate-800 text-white shadow-sm">
                    <div className="grid gap-6 p-6 lg:grid-cols-[1.5fr_1fr]">
                      <div className="space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-300">End of Day Report</p>
                        <h3 className="text-3xl font-bold">
                          {dailySalesReport.branch_name || 'All Branches'}
                        </h3>
                        <p className="text-sm text-slate-300">
                          Daily sales, bank receipts, and shop expenses in one closing view.
                        </p>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                          <p className="text-xs uppercase tracking-wide text-slate-300">Report Date</p>
                          <p className="mt-2 text-lg font-semibold">{dailySalesReport.report_date}</p>
                        </div>
                        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                          <p className="text-xs uppercase tracking-wide text-slate-300">Day</p>
                          <p className="mt-2 text-lg font-semibold">{dailySalesReport.day_name}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                    <div className="rounded-2xl border bg-emerald-50 p-5 shadow-sm">
                      <p className="text-sm font-medium text-emerald-700">Cash Sales</p>
                      <p className="mt-2 text-3xl font-bold text-emerald-900">{currencySymbol}{dailySalesReport.summary.cash_sales_total.toFixed(2)}</p>
                    </div>
                    <div className="rounded-2xl border bg-blue-50 p-5 shadow-sm">
                      <p className="text-sm font-medium text-blue-700">Bank / SP Sales</p>
                      <p className="mt-2 text-3xl font-bold text-blue-900">{currencySymbol}{dailySalesReport.summary.bank_sales_total.toFixed(2)}</p>
                    </div>
                    <div className="rounded-2xl border bg-amber-50 p-5 shadow-sm">
                      <p className="text-sm font-medium text-amber-700">Total Sales</p>
                      <p className="mt-2 text-3xl font-bold text-amber-900">{currencySymbol}{dailySalesReport.summary.total_sales.toFixed(2)}</p>
                    </div>
                    <div className="rounded-2xl border bg-rose-50 p-5 shadow-sm">
                      <p className="text-sm font-medium text-rose-700">Cash Expenses</p>
                      <p className="mt-2 text-3xl font-bold text-rose-900">{currencySymbol}{dailySalesReport.summary.cash_expenses_total.toFixed(2)}</p>
                    </div>
                    <div className="rounded-2xl border bg-lime-50 p-5 shadow-sm">
                      <p className="text-sm font-medium text-lime-700">Cash Remaining</p>
                      <p className="mt-2 text-3xl font-bold text-lime-900">{currencySymbol}{dailySalesReport.summary.cash_in_hand_remaining.toFixed(2)}</p>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <div className="rounded-2xl border bg-white p-5 shadow-sm">
                      <p className="text-sm font-medium text-slate-500">Sales Entries</p>
                      <p className="mt-2 text-2xl font-bold text-slate-900">{dailySalesReport.sales.length}</p>
                    </div>
                    <div className="rounded-2xl border bg-white p-5 shadow-sm">
                      <p className="text-sm font-medium text-slate-500">Expense Entries</p>
                      <p className="mt-2 text-2xl font-bold text-slate-900">{dailySalesReport.expenses.length}</p>
                    </div>
                    <div className="rounded-2xl border bg-white p-5 shadow-sm">
                      <p className="text-sm font-medium text-slate-500">Average Sale Value</p>
                      <p className="mt-2 text-2xl font-bold text-slate-900">
                        {currencySymbol}
                        {(dailySalesReport.summary.total_sales / Math.max(dailySalesReport.sales.length, 1)).toFixed(2)}
                      </p>
                    </div>
                    <div className="rounded-2xl border bg-white p-5 shadow-sm">
                      <p className="text-sm font-medium text-slate-500">Bank Net Position</p>
                      <p className="mt-2 text-2xl font-bold text-slate-900">{currencySymbol}{dailySalesReport.summary.bank_remaining.toFixed(2)}</p>
                    </div>
                  </div>

                  <div className="grid gap-6 xl:grid-cols-[1.5fr_1fr]">
                    <div className="rounded-2xl border bg-white shadow-sm">
                      <div className="border-b px-5 py-4">
                        <h4 className="text-lg font-semibold">Sales Breakdown</h4>
                        <p className="text-sm text-muted-foreground">Each line shows how the day’s sales were received in cash or bank.</p>
                      </div>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50 hover:bg-slate-50">
                              <TableHead>Description</TableHead>
                              <TableHead className="text-right">Cash</TableHead>
                              <TableHead className="text-right">SP / Bank</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {dailySalesReport.sales.length ? dailySalesReport.sales.map((row, index) => (
                              <TableRow key={`sale-${row.sale_id}-${index}`}>
                                <TableCell className="font-medium text-slate-900">{row.description}</TableCell>
                                <TableCell className="text-right">{row.cash_amount > 0 ? `${currencySymbol}${row.cash_amount.toFixed(2)}` : '—'}</TableCell>
                                <TableCell className="text-right">{row.bank_amount > 0 ? `${currencySymbol}${row.bank_amount.toFixed(2)}` : '—'}</TableCell>
                              </TableRow>
                            )) : (
                              <TableRow>
                                <TableCell colSpan={3} className="py-8 text-center text-muted-foreground">No sales found for the selected date.</TableCell>
                              </TableRow>
                            )}
                            <TableRow className="bg-slate-50 hover:bg-slate-50">
                              <TableCell className="font-semibold">Sales Total</TableCell>
                              <TableCell className="text-right font-semibold">{currencySymbol}{dailySalesReport.summary.cash_sales_total.toFixed(2)}</TableCell>
                              <TableCell className="text-right font-semibold">{currencySymbol}{dailySalesReport.summary.bank_sales_total.toFixed(2)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="rounded-2xl border bg-white shadow-sm">
                        <div className="border-b px-5 py-4">
                          <h4 className="text-lg font-semibold">Expenses</h4>
                          <p className="text-sm text-muted-foreground">Expenses posted on the same day.</p>
                        </div>
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-50 hover:bg-slate-50">
                                <TableHead>Description</TableHead>
                                <TableHead className="text-right">Cash</TableHead>
                                <TableHead className="text-right">Bank</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {dailySalesReport.expenses.length ? dailySalesReport.expenses.map((row, index) => (
                                <TableRow key={`expense-${row.expense_id}-${index}`}>
                                  <TableCell className="font-medium text-slate-900">{row.description}</TableCell>
                                  <TableCell className="text-right">{row.cash_amount > 0 ? `${currencySymbol}${row.cash_amount.toFixed(2)}` : '—'}</TableCell>
                                  <TableCell className="text-right">{row.bank_amount > 0 ? `${currencySymbol}${row.bank_amount.toFixed(2)}` : '—'}</TableCell>
                                </TableRow>
                              )) : (
                                <TableRow>
                                  <TableCell colSpan={3} className="py-8 text-center text-muted-foreground">No expenses found for the selected date.</TableCell>
                                </TableRow>
                              )}
                              <TableRow className="bg-slate-50 hover:bg-slate-50">
                                <TableCell className="font-semibold">Expense Total</TableCell>
                                <TableCell className="text-right font-semibold">{currencySymbol}{dailySalesReport.summary.cash_expenses_total.toFixed(2)}</TableCell>
                                <TableCell className="text-right font-semibold">{currencySymbol}{dailySalesReport.summary.bank_expenses_total.toFixed(2)}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </div>
                      </div>

                      <div className="rounded-2xl border bg-slate-950 p-5 text-white shadow-sm">
                        <h4 className="text-lg font-semibold">Closing Summary</h4>
                        <div className="mt-4 space-y-3 text-sm">
                          <div className="flex items-center justify-between border-b border-white/10 pb-3">
                            <span className="text-slate-300">Total Sales Collected</span>
                            <span className="text-base font-semibold">{currencySymbol}{dailySalesReport.summary.total_sales.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center justify-between border-b border-white/10 pb-3">
                            <span className="text-slate-300">Cash In Hand Before Expenses</span>
                            <span className="text-base font-semibold">{currencySymbol}{dailySalesReport.summary.cash_sales_total.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center justify-between border-b border-white/10 pb-3">
                            <span className="text-slate-300">Cash Used for Expenses</span>
                            <span className="text-base font-semibold">{currencySymbol}{dailySalesReport.summary.cash_expenses_total.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center justify-between border-b border-white/10 pb-3">
                            <span className="text-slate-300">Bank-side Expenses</span>
                            <span className="text-base font-semibold">{currencySymbol}{dailySalesReport.summary.bank_expenses_total.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center justify-between border-b border-white/10 pb-3">
                            <span className="text-slate-300">Bank Net Position</span>
                            <span className="text-base font-semibold">{currencySymbol}{dailySalesReport.summary.bank_remaining.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center justify-between pt-1">
                            <span className="text-base font-medium text-emerald-300">Cash Remaining</span>
                            <span className="text-2xl font-bold text-emerald-300">{currencySymbol}{dailySalesReport.summary.cash_in_hand_remaining.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <StatementDetailDialog
        open={detailDialog.open}
        onOpenChange={(open) => setDetailDialog(prev => ({ ...prev, open }))}
        type={detailDialog.type}
        startDate={detailDialog.startDate}
        endDate={detailDialog.endDate}
        label={detailDialog.label}
        categoryId={detailDialog.categoryId}
        categoryName={detailDialog.categoryName}
        currencySymbol={currencySymbol}
        systemTimezone={systemTimezone}
      />
    </div>
  );
}
